/**
 * Keeping a picture, shared by every room that can save one.
 *
 * The thumbnail is rendered from the scene rather than grabbed off the visible
 * canvas, so what gets kept is the whole drawing — including the parts the
 * child has scrolled away from — and the marks are stored beside it so the
 * picture can be opened and added to later.
 */

import type { Scene } from './scene.ts';
import { encodeScene } from './scene.ts';
import { renderScene } from '../engine/snapshot.ts';
import { token } from '../engine/palette.ts';
import { requestPersistence, StorageFullError, store } from './store.ts';

const THUMB_SIZE = 640;
const QUALITY = 0.78;

export type KeepResult = { ok: true } | { ok: false; message: string };

export async function keepPicture(scene: Scene): Promise<KeepResult> {
  if (scene.strokes.length === 0) return { ok: false, message: 'There is nothing to keep yet' };

  const shot = renderScene(scene, THUMB_SIZE, token('--paper'));
  if (!shot) return { ok: false, message: 'There is nothing to keep yet' };

  try {
    const db = await store();
    await db.save({
      kind: scene.kind,
      thumb: shot.canvas.toDataURL('image/jpeg', QUALITY),
      w: shot.width,
      h: shot.height,
      scene: encodeScene(scene),
    });
    // Only worth asking once there is something to protect.
    void requestPersistence();
    return { ok: true };
  } catch (err) {
    if (err instanceof StorageFullError) {
      return { ok: false, message: 'No room left — save your pictures to your computer' };
    }
    return { ok: false, message: 'That did not save. Try once more' };
  }
}
